import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@helpers/api";
import { useCommunity } from "@contexts/CommunityContext";

async function ensurePdfJs() {
    if (window.pdfjsLib) return window.pdfjsLib;
    await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.min.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
    });
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
    return window.pdfjsLib;
}

export const useMaterial = () => {
    const { slug, materialId } = useParams();
    const { community, loading: communityLoading } = useCommunity();
    const navigate = useNavigate();

    const [material, setMaterial] = useState({});
    const [loading, setLoading] = useState(true);
    const [zoom, setZoom] = useState(1);
    const [pdfDocument, setPdfDocument] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentCommunity, setCurrentCommunity] = useState();
    const [isFullscreen, setIsFullscreen] = useState(false);
    
    const REF_materialModal = useRef();
    const fullscreenRef = useRef();

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    useEffect(() => {
        if (loading) handleLoadContent();
    }, [loading]);

    useEffect(() => {
        if (!loading) setLoading(true);
    }, [materialId]);

    useEffect(() => {
        const comm = community.getBySlug(slug);
        if (comm) setCurrentCommunity({ ...comm });
    }, [slug, communityLoading]);

    useEffect(() => {
        let cancelled = false;
        async function loadPdf() {
            if (!materialId || !material?.path) return;
            setPdfDocument(null);
            try {
                const pdfjsLib = await ensurePdfJs();
                const response = await fetch(material.path, { credentials: 'omit' });
                if (!response.ok || cancelled) return;
                const data = await response.arrayBuffer();
                if (cancelled) return;
                const loadingTask = pdfjsLib.getDocument({ data });
                const pdf = await loadingTask.promise;
                if (!cancelled) setPdfDocument(pdf);
            } catch (e) {
                console.log(e);
            }
        }
        loadPdf();
        return () => { cancelled = true; };
    }, [materialId, material?.path]);

    async function handleLoadContent() {
        try {
            const { data } = await api.get(`material/${materialId}`);
            const can_access =
                data.can_access !== undefined
                    ? data.can_access
                    : data.availability?.available !== false;
            setMaterial({ ...data, can_access });
        } catch (e) {
            console.log(e);
        }
        setLoading(false);
    }

    async function handleDownload() {
        if (!materialId) return;

        try {
            const res = await api.get(`material/${materialId}/getDownloadUrl`);
            const downloadUrl = res?.data?.download_url;
            if (!downloadUrl) {
                console.error('Resposta inválida ao obter URL de download', res);
                return;
            }

            const response = await fetch(downloadUrl, { credentials: 'omit' });
            if (!response.ok) {
                console.error('Falha ao baixar o arquivo', response.status);
                return;
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${material?.name || 'material'}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.log(e);
        }
    }

    function handleOpenEditMaterial() {
        REF_materialModal.current.open(material, (result) => {
            if (result?.id) setLoading(true);
        });
    }

    function handlePageClick(pageNum) {
        const target = document.querySelector(`[data-page="${pageNum}"]`);
        const container = document.querySelector('.material-container');
        if (target && container) {
            const containerRect = container.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            const relativeTop = targetRect.top - containerRect.top + container.scrollTop;

            container.scrollTo({
                top: relativeTop,
                behavior: 'smooth'
            });
        }
    }

    function handleGoBack() {
        navigate(-1);
    }

    function handleToggleFullscreen() {
        if (!document.fullscreenElement) {
            if (fullscreenRef.current?.requestFullscreen) {
                fullscreenRef.current.requestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
    }

    return {
        material,
        loading,
        zoom,
        setZoom,
        pdfDocument,
        currentPage,
        setCurrentPage,
        currentCommunity,
        isFullscreen,
        REF_materialModal,
        fullscreenRef,
        handleDownload,
        handleOpenEditMaterial,
        handlePageClick,
        handleGoBack,
        handleToggleFullscreen
    };
};
